import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { getManagerLevel } from "../../api/account/getManagerLevel";
import { getActivity, getActivityProcedures } from "../../api/activity";
import { ActivityDetailForm } from "../../components/Activity";
import { Files } from "../../components/Files";
import { ManagersList } from "../../components/Managers";
import ProcedureList from "../../components/Procedure/ProcedureList";
import { useTranslationHook } from "../../hooks";
import { setLoading } from "../../store/UI";
import { ManagerLevel } from "../../types/model/account";
import { Activity } from "../../types/model/activity";
import { Procedure } from "../../types/model/procedure";
import { StoreInterfaces } from "../../types/store";

const DetailsActivity = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslationHook();

  const { accountId, projectId, activityId } = useParams<{
    accountId: string;
    projectId: string;
    activityId: string;
  }>();

  const session = useSelector((state: StoreInterfaces) => state.session);

  const [activityData, setActivityData] = useState<Activity | null>(null);
  const [managerLevel, setManagerLevel] = useState<ManagerLevel>();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const isTenderUser =
    !!session?.accessToken &&
    !session.isAdministrator &&
    !session.isManager;

  const readOnly =
    isTenderUser ||
    (!!managerLevel &&
      managerLevel.level !== "account" &&
      managerLevel.level !== "project" &&
      managerLevel.level !== "activity");
  
  const fetchManagerLevel = async () => {
    try {
      const data = await getManagerLevel(
        Number(accountId),
        Number(projectId),
        Number(activityId),
      );
      setManagerLevel(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchActivity = async () => {
    dispatch(setLoading(true));
    try {
      const data = await getActivity(Number(projectId), Number(activityId));
      setActivityData(data);
      enqueueSnackbar(t("generic.data-loaded"), { variant: "success" });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const fetchProcedures = async () => {
    if (!projectId || !activityId) return;
    setProceduresLoading(true);
    try {
      const data = await getActivityProcedures(
        Number(projectId),
        Number(activityId),
      );
      setProcedures(data);
    } catch (error) {
      console.error(error);
    } finally {
      setProceduresLoading(false);
    }
  };

  useEffect(() => {
    fetchManagerLevel();
    fetchActivity();
    fetchProcedures();
  }, [activityId]);

  const handleAddProcedure = () => {
    if (!activityData) return;
    navigate(
      `/accounts/${accountId}/projects/${projectId}/activities/${activityId}/procedures/add-procedure`,
    );
  };

  const handleProcedureSelect = (procedure: Procedure) => {
    if (!activityData) return;
    navigate(
      `/accounts/${accountId}/projects/${projectId}/activities/${activityId}/procedures/${procedure.id}`,
    );
  };

  if (!activityData) return <CircularProgress />;

  const tabs = [
    {
      id: "details",
      label: t("activities.tabs.detail"),
      component: managerLevel && (
        <ActivityDetailForm
          activity={activityData}
          accountId={activityData.accountId}
          projectId={activityData.projectId}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "procedures",
      label: t("activities.tabs.procedures"),
      component: managerLevel && (
        <ProcedureList
          procedures={procedures}
          loading={proceduresLoading}
          onProcedureSelected={handleProcedureSelect}
          onAddProcedure={handleAddProcedure}
          onRefresh={fetchProcedures}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "managers",
      label: t("activities.tabs.managers"),
      component: managerLevel && (
        <ManagersList
          accountId={Number(accountId)}
          projectId={Number(projectId)}
          activityId={Number(activityId)}
          managerLevel={managerLevel}
          isObjectClosedOrCanceled={activityData.closed || activityData.deleted}
          readOnly={readOnly}
        />
      ),
    },
    {
      id: "files",
      label: t("generic.files"),
      component: managerLevel && (
        <Files
          accountId={activityData.accountId}
          projectId={activityData.projectId}
          activityId={Number(activityId)}
          readOnly={readOnly}
        />
      ),
    },
  ];

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("generic.activity")} - {activityData.name}
        </Typography>
        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.id} label={tab.label} />
        ))}
      </Tabs>

      <Box sx={{ p: 3 }}>
        {tabs.map((tab, i) => (
          <Fade
            key={tab.id}
            in={tabIndex === i}
            timeout={{ enter: 200, exit: 0 }}
            unmountOnExit
          >
            <Box>{tab.component}</Box>
          </Fade>
        ))}
      </Box>
    </Paper>
  );
};

export default DetailsActivity;
